// React
import React from 'react';

// Mantine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { MRT_ColumnDef, MRT_TableOptions } from 'mantine-react-table';

// Refine
import type { ListProps } from '@refinedev/mantine';

// Custom Components
import { EditRowButton } from 'components/common/EditRowButton';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';
import { OrganizationColumns } from '../../columns/Organization';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { CreateModal, EditModal, TableActionButton } from 'components/common';

// Interfaces
import { Organization } from 'interfaces';

// API
import {
  useCreateOrganization,
  useListOrganizations,
  useUpdateOrganization,
} from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';

// Auth
import { useIsAdmin } from 'hooks/useIsAdmin';

// Store
import { useCurrentUserStore } from 'store/UserStore';

import { IconPlus } from '@tabler/icons-react';

export const OrganizationsManagement: React.FC<ListProps> = () => {
  const title = 'Organization Management';
  useDocumentTitle('Organizations | QBilling');
  const isAdmin = useIsAdmin();
  const { organizationID } = useCurrentUserStore();

  const {
    data: organizations = [] as Organization[],
    isLoading,
    isFetching,
    isError,
    error: organizationError,
  } = useListOrganizations({ isAdmin, organizationID });

  const createOrganization = useCreateOrganization();
  const updateOrganization = useUpdateOrganization();

  const handleCreateOrganization = async ({
    table,
    values,
  }: CreateMutationDataParams<Organization>) =>
    createMutationData<Organization>({
      mutation: createOrganization,
      table,
      values,
    });

  const handleEditOrganization = async ({
    row,
    values,
    table,
  }: UpdateMutationDataParams<Organization>) =>
    updateMutationData<Organization>({
      row,
      mutation: updateOrganization,
      table,
      values,
    });

  const data: Organization[] = (organizations as Organization[]) ?? [];
  const columns: MRT_ColumnDef<Organization>[] = OrganizationColumns();

  const tableOptions: MRT_TableOptions<Organization> = {
    data,
    columns,
    state: {
      isLoading: isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: true,
    enableHiding: true,
    mantineLoadingOverlayProps: { visible: isLoading },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: organizationError?.message });
    },
    mantineEditRowModalProps: {
      size: 'lg',
      overlayProps: {
        opacity: 0.15,
        blur: 5,
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({ row, table }) => {
          return <EditRowButton row={row} table={table} type="organization" />;
        },
      },
    },
    onCreatingRowSave: handleCreateOrganization,
    onEditingRowSave: handleEditOrganization,
    renderTopToolbarCustomActions: ({ table }) => (
      <TableActionButton
        leftIcon={<IconPlus />}
        onClick={() => table.setCreatingRow(true)}
        dataTestId="create-organization-button"
        isLoading={isLoading}
      >
        New Organization
      </TableActionButton>
    ),
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => {
      return CreateModal<Organization>({
        table,
        row,
        internalEditComponents,
        type: 'organization',
      });
    },
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => {
      return EditModal<Organization>({
        table,
        row,
        internalEditComponents,
        type: 'organization',
      });
    },
  };
  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="organizationManagement"
    />
  );
};
